import { supabase } from '@/integrations/supabase/client';

export interface UserRow {
  id: string;
  full_name: string;
  email: string;
  role?: string;
  organization_id?: string | null;
  avatar_url?: string | null;
  is_active?: boolean | null;
  staff_id?: string | null;
  position?: string | null;
  joining_date?: string | null;
  salary_type?: string | null;
}

export interface OrgContext {
  organizationId?: string | null;
  isSuperAdmin?: boolean;
}

export interface UpdateUserEmailResult {
  id: string;
  email: string;
  auth_email?: string | null;
  email_confirmed_at?: string | null;
  auth_synced?: boolean;
  profile_synced?: boolean;
}

export async function fetchOrgUsers(
  ctx: OrgContext,
  opts?: { roles?: string[]; excludeTestEmails?: boolean }
): Promise<UserRow[]> {
  let query = supabase
    .from('users')
    .select('id, full_name, email, role, organization_id, avatar_url, is_active, position, joining_date, salary_type')
    .order('full_name');

  if (ctx.organizationId && !ctx.isSuperAdmin) {
    query = query.eq('organization_id', ctx.organizationId);
  }

  if (opts?.roles?.length) {
    query = query.in('role', opts.roles);
  }

  if (opts?.excludeTestEmails) {
    query = query.not('email', 'ilike', '%example.com%');
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data || []) as UserRow[];
}

export async function fetchOrgUserIds(ctx: OrgContext): Promise<string[]> {
  const users = await fetchOrgUsers(ctx);
  return users.map((u) => u.id);
}

export async function fetchUserById(userId: string): Promise<UserRow | null> {
  const { data, error } = await supabase
    .from('users')
    .select('id, full_name, email, role, organization_id, avatar_url, is_active, position, joining_date, salary_type')
    .eq('id', userId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }
  return data as UserRow;
}

export async function deleteUser(userId: string): Promise<void> {
  const { error } = await supabase.from('users').delete().eq('id', userId);
  if (error) throw error;
}

export async function updateUserEmail(
  userId: string,
  email: string,
): Promise<UpdateUserEmailResult> {
  const { data, error } = await supabase.functions.invoke('resend-confirmation', {
    body: {
      action: 'update-email',
      userId,
      email: email.trim(),
    },
  });

  if (error) {
    const message = typeof data?.error === 'string' ? data.error : error.message;
    throw new Error(message);
  }

  if (!data?.success || !data?.user?.email) {
    throw new Error(typeof data?.error === 'string' ? data.error : 'Email update failed');
  }

  return data.user as UpdateUserEmailResult;
}
